'use client';
import type { Assign } from '@ark-ui/react';
import { SegmentGroup } from '@ark-ui/react';
import {
  type SegmentGroupVariantProps,
  segmentGroup,
} from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import {
  BIEvents,
  type BIEventProps,
  serializeBIEventData,
} from '@terra/ui-telemetry/bi-analytics';
import { forwardRef } from 'react';
import { createStyleContext } from './utils/create-style-context';

const { withProvider, withContext } = createStyleContext(segmentGroup);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, SegmentGroup.RootProviderProps>,
    SegmentGroupVariantProps
  >
>(SegmentGroup.RootProvider, 'root');

export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, SegmentGroup.RootProps>,
    SegmentGroupVariantProps
  >
>(SegmentGroup.Root, 'root');

export const Indicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, SegmentGroup.IndicatorProps>
>(SegmentGroup.Indicator, 'indicator');

export const ItemControl = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, SegmentGroup.ItemControlProps>
>(SegmentGroup.ItemControl, 'itemControl');

type ItemAnalyticsProps = {
  bi?: BIEventProps;
};

const BaseItem = withContext<
  HTMLLabelElement,
  Assign<HTMLStyledProps<'label'>, SegmentGroup.ItemProps>
>(SegmentGroup.Item, 'item');

export type ItemProps = ComponentProps<typeof BaseItem> & ItemAnalyticsProps;

export const Item = forwardRef<HTMLLabelElement, ItemProps>((props, ref) => {
  const { bi, ...rest } = props;
  const { eventType = BIEvents.CLICK, eventData } = bi || {};

  return (
    <BaseItem
      ref={ref}
      data-bi-unmask="true"
      data-analytics-type={eventType}
      data-analytics-props={serializeBIEventData(eventData)}
      {...rest}
    />
  );
});

Item.displayName = 'SegmentGroup.Item';

export const ItemText = withContext<
  HTMLSpanElement,
  Assign<HTMLStyledProps<'span'>, SegmentGroup.ItemTextProps>
>(SegmentGroup.ItemText, 'itemText');

export const Label = withContext<
  HTMLLabelElement,
  Assign<HTMLStyledProps<'label'>, SegmentGroup.LabelProps>
>(SegmentGroup.Label, 'label');

export {
  SegmentGroupContext as Context,
  SegmentGroupItemHiddenInput as ItemHiddenInput,
} from '@ark-ui/react';
